import React, { useState, useEffect } from 'react';
import { GraduationCap, Users, Calendar, Plus, Search, MapPin, Clock, Euro, Pencil, Trash2, UserPlus, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import NewCourseModal from '../components/NewCourseModal';
import CourseEnrollmentModal from '../components/CourseEnrollmentModal';

interface Course {
  id: string;
  name: string;
  description: string;
  course_type: string;
  status: string;
  date: string;
  end_date: string | null;
  time: string;
  duration_minutes: number;
  location: string;
  city: string;
  max_participants: number;
  participant_count: number;
  price: number;
  instructor: string;
  topics: string[];
  image_url: string | null;
  created_at: string;
}

const COURSE_TYPE_LABELS: Record<string, string> = {
  workshop: 'Workshop',
  seminario: 'Seminario',
  webinar: 'Webinar',
  multi_sessione: 'Multi Sessione',
};

const COURSE_TYPE_COLORS: Record<string, string> = {
  workshop: 'bg-blue-100 text-blue-800',
  seminario: 'bg-teal-100 text-teal-800',
  webinar: 'bg-amber-100 text-amber-800',
  multi_sessione: 'bg-rose-100 text-rose-800',
};

const STATUS_LABELS: Record<string, string> = {
  programmato: 'Programmato',
  'in corso': 'In Corso',
  completato: 'Completato',
  annullato: 'Annullato',
};

const STATUS_COLORS: Record<string, string> = {
  programmato: 'bg-sky-100 text-sky-800',
  'in corso': 'bg-green-100 text-green-800',
  completato: 'bg-gray-100 text-gray-800',
  annullato: 'bg-red-100 text-red-800',
};

const CorsiPerSingle = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [isNewCourseModalOpen, setIsNewCourseModalOpen] = useState(false);
  const [enrollmentCourse, setEnrollmentCourse] = useState<Course | null>(null);
  const [editingStatus, setEditingStatus] = useState<string | null>(null);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const { data, error } = await supabase
        .from('courses')
        .select('*')
        .order('date', { ascending: false });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteCourse = async (id: string) => {
    if (!confirm('Eliminare questo corso? Anche tutte le iscrizioni saranno eliminate.')) return;

    try {
      const { error } = await supabase.from('courses').delete().eq('id', id);
      if (error) throw error;
      loadCourses();
    } catch (error) {
      console.error('Error deleting course:', error);
    }
  };

  const updateCourseStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase.from('courses').update({ status }).eq('id', id);
      if (error) throw error;
      setEditingStatus(null);
      loadCourses();
    } catch (error) {
      console.error('Error updating course status:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.instructor.toLowerCase().includes(searchTerm.toLowerCase()) ||
      course.city.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || course.course_type === filterType;
    const matchesStatus = filterStatus === 'all' || course.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const stats = {
    total: courses.length,
    programmati: courses.filter(c => c.status === 'programmato').length,
    inCorso: courses.filter(c => c.status === 'in corso').length,
    completati: courses.filter(c => c.status === 'completato').length,
    totaleIscritti: courses.reduce((sum, c) => sum + c.participant_count, 0),
  };

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-white">Corsi per Single</h1>
            <p className="text-white/80 mt-1">Gestisci workshop, seminari e corsi dedicati ai single</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Totale Corsi</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.total}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <GraduationCap className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Programmati</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.programmati}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Calendar className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">In Corso</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.inCorso}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Clock className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Completati</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.completati}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Totale Iscritti</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats.totaleIscritti}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Users className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Cerca per nome, istruttore o citta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            >
              <option value="all">Tutti i Tipi</option>
              <option value="workshop">Workshop</option>
              <option value="seminario">Seminario</option>
              <option value="webinar">Webinar</option>
              <option value="multi_sessione">Multi Sessione</option>
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent"
            >
              <option value="all">Tutti gli Stati</option>
              <option value="programmato">Programmati</option>
              <option value="in corso">In Corso</option>
              <option value="completato">Completati</option>
              <option value="annullato">Annullati</option>
            </select>
            <button
              onClick={() => setIsNewCourseModalOpen(true)}
              className="px-6 py-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors flex items-center gap-2 font-medium shadow-sm whitespace-nowrap"
            >
              <Plus className="h-5 w-5" />
              Nuovo Corso
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-rose-500" />
          </div>
        ) : filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <GraduationCap className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun Corso Trovato
            </h3>
            <p className="text-gray-600 mb-6">
              {courses.length === 0
                ? "Inizia creando il tuo primo corso per single"
                : "Nessun corso corrisponde ai criteri di ricerca"}
            </p>
            {courses.length === 0 && (
              <button
                onClick={() => setIsNewCourseModalOpen(true)}
                className="bg-rose-600 text-white px-4 py-2 rounded-lg hover:bg-rose-700 transition-colors inline-flex items-center gap-2"
              >
                <Plus className="h-5 w-5" />
                Crea Primo Corso
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Corso</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Tipo</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Data</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Luogo</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Iscritti</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Prezzo</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Stato</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCourses.map((course) => (
                  <tr key={course.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="font-medium text-gray-900">{course.name}</div>
                      {course.instructor && (
                        <div className="text-xs text-gray-500 mt-0.5">Istruttore: {course.instructor}</div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${COURSE_TYPE_COLORS[course.course_type] || 'bg-gray-100 text-gray-800'}`}>
                        {COURSE_TYPE_LABELS[course.course_type] || course.course_type}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      <div>{formatDate(course.date)}</div>
                      {course.end_date && (
                        <div className="text-xs text-gray-400">al {formatDate(course.end_date)}</div>
                      )}
                      {course.time && <div className="text-xs text-gray-400">{course.time}</div>}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {course.location && <div>{course.location}</div>}
                      <div className="text-xs text-gray-400">{course.city}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-medium text-gray-900">{course.participant_count}</span>
                        <span className="text-xs text-gray-400">/ {course.max_participants}</span>
                      </div>
                      <div className="w-20 h-1.5 bg-gray-200 rounded-full mt-1">
                        <div
                          className="h-full bg-rose-500 rounded-full"
                          style={{ width: `${Math.min(100, (course.participant_count / course.max_participants) * 100)}%` }}
                        />
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm">
                      {course.price > 0 ? (
                        <span className="font-medium text-gray-900">{course.price.toFixed(2)} EUR</span>
                      ) : (
                        <span className="text-green-600 font-medium">Gratuito</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {editingStatus === course.id ? (
                        <select
                          value={course.status}
                          onChange={(e) => updateCourseStatus(course.id, e.target.value)}
                          onBlur={() => setEditingStatus(null)}
                          autoFocus
                          className="text-xs rounded border-gray-300 focus:ring-rose-500 focus:border-rose-500"
                        >
                          <option value="programmato">Programmato</option>
                          <option value="in corso">In Corso</option>
                          <option value="completato">Completato</option>
                          <option value="annullato">Annullato</option>
                        </select>
                      ) : (
                        <button
                          onClick={() => setEditingStatus(course.id)}
                          className={`px-2 py-1 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 ${STATUS_COLORS[course.status] || 'bg-gray-100 text-gray-800'}`}
                        >
                          {STATUS_LABELS[course.status] || course.status}
                        </button>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-1">
                        <button
                          onClick={() => setEnrollmentCourse(course)}
                          className="text-rose-600 hover:text-rose-700 p-1.5 hover:bg-rose-50 rounded transition-colors"
                          title="Gestisci Iscritti"
                        >
                          <UserPlus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteCourse(course.id)}
                          className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded transition-colors"
                          title="Elimina Corso"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <NewCourseModal
        isOpen={isNewCourseModalOpen}
        onClose={() => setIsNewCourseModalOpen(false)}
        onSuccess={loadCourses}
      />

      {enrollmentCourse && (
        <CourseEnrollmentModal
          isOpen={true}
          onClose={() => setEnrollmentCourse(null)}
          courseId={enrollmentCourse.id}
          courseName={enrollmentCourse.name}
          onUpdate={loadCourses}
        />
      )}
    </div>
  );
};

export default CorsiPerSingle;
